import EVENT from '../enums/event.js';
import KEY from '../enums/key.js';
import STATE from '../enums/state.js';
import TabTrapper from '../utils/accessibility.tab-trapper.js';
import dispatchEvent from '../utils/dispatch-event.js';

const selectors = {
  focusable:
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]',
};

class Drawer {
  /**
   *
   * @param {Element} $element
   */
  constructor($element) {
    this.$drawer = $element;
    this.$focusableElements = this.$drawer.querySelectorAll(
      selectors.focusable
    );
    this.$openTriggers = [...document.querySelectorAll(`[data-drawer-open]`)];
    this.$closeTriggers = [...document.querySelectorAll(`[data-drawer-close]`)];
    this.$toggleTriggers = [
      ...document.querySelectorAll(`[data-drawer-toggle]`),
    ];

    this.name = this.$drawer.dataset.drawer;

    this.registerEventListeners();
    this.hideFocusableElements();
  }

  /**
   * @returns {'closed'|'open'}
   */
  get state() {
    return this.$drawer.dataset.state;
  }

  set state(state) {
    this.$drawer.dataset.state = state;
  }

  registerEventListeners() {
    document.addEventListener(EVENT.DRAWER.OPEN, event => {
      if (event.detail.drawer !== this.name) {
        this.closeDrawer();
        return;
      }

      this.$activeTrigger = event.target;

      if (this.state !== STATE.DRAWER.OPEN) {
        this.openDrawer();
      }
    });

    document.addEventListener(EVENT.DRAWER.CLOSE, event => {
      if (event.detail.drawer !== this.name) {
        this.closeDrawer();
        return;
      }

      this.$activeTrigger = event.target;

      if (this.state === STATE.DRAWER.OPEN) {
        this.closeDrawer();
      }
    });

    document.addEventListener(EVENT.DRAWER.TOGGLE, event => {
      if (event.detail.drawer !== this.name) {
        this.closeDrawer();
        return;
      }

      this.$activeTrigger = event.target;

      if (this.state === STATE.DRAWER.OPEN) {
        this.closeDrawer();
        return;
      }

      this.openDrawer();
    });

    document.addEventListener(EVENT.OVERLAY.DEACTIVATE, () =>
      this.closeDrawer()
    );

    this.$openTriggers.forEach($openTrigger => {
      $openTrigger.addEventListener('click', event => {
        event.preventDefault();

        dispatchEvent(EVENT.DRAWER.OPEN, $openTrigger, {
          detail: {
            drawer: $openTrigger.dataset.drawerOpen,
          },
        });
      });
    });

    this.$closeTriggers.forEach($closeTrigger => {
      $closeTrigger.addEventListener('click', event => {
        event.preventDefault();

        dispatchEvent(EVENT.DRAWER.CLOSE, $closeTrigger, {
          detail: {
            drawer: $closeTrigger.dataset.drawerClose,
          },
        });
      });
    });

    this.$toggleTriggers.forEach($toggleTrigger => {
      $toggleTrigger.addEventListener('click', event => {
        event.preventDefault();

        dispatchEvent(EVENT.DRAWER.TOGGLE, $toggleTrigger, {
          detail: {
            drawer: $toggleTrigger.dataset.drawerToggle,
          },
        });
      });
    });
  }

  closeDrawer() {
    if (this.state === STATE.DRAWER.CLOSED) {
      return;
    }

    this.state = STATE.DRAWER.CLOSED;
    this.hideFocusableElements();

    if (this.$activeTrigger) {
      this.$activeTrigger.dataset.state = STATE.DRAWER.CLOSED;
    }

    dispatchEvent(EVENT.OVERLAY.DEACTIVATE, this.$drawer);

    // Controlling focus
    if (this.focusElement) {
      this.focusElement.focus();
    }

    if (this.tabTrapper) {
      this.tabTrapper.close();
    }

    document.removeEventListener('keydown', event => {
      if (event.key === KEY.ESCAPE) {
        this.closeDrawer();
      }
    });
  }

  openDrawer() {
    this.showFocusableElements();
    this.state = STATE.DRAWER.OPEN;

    if (this.$activeTrigger) {
      this.$activeTrigger.dataset.state = STATE.DRAWER.OPEN;
    }

    dispatchEvent(EVENT.OVERLAY.ACTIVATE, this.$drawer, {
      detail: {
        lockScroll: true,
      },
    });

    // Controlling focus
    this.focusElement = document.activeElement;
    this.$drawer.focus();

    this.tabTrapper = new TabTrapper(this.$drawer);

    document.addEventListener('keydown', event => {
      if (event.key === KEY.ESCAPE) {
        this.closeDrawer();
      }
    });
  }

  hideFocusableElements() {
    this.$focusableElements.forEach(focusableElement => {
      const el = focusableElement;
      el.tabIndex = -1;
    });
  }

  showFocusableElements() {
    this.$focusableElements.forEach(focusableElement => {
      const el = focusableElement;
      el.tabIndex = 0;
    });
  }
}

export default Drawer;
